import { useEffect, useState } from "react";
import { Smartphone, Share, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const DISMISS_KEY = "bvsm:pwa-install-dismissed";
const DISMISS_DAYS = 7;

/** Detects standalone display mode (app already installed). */
function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isAndroid() {
  if (typeof window === "undefined") return false;
  return /Android/i.test(window.navigator.userAgent || "");
}

function wasRecentlyDismissed() {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

/**
 * Subtle PWA install prompt that appears on the Painel for mobile users.
 *
 * - Android/Chrome/Edge: captures the `beforeinstallprompt` event and offers
 *   a single-tap install button.
 * - iOS Safari: shows manual "Compartilhar → Adicionar à Tela de Início"
 *   instructions (Safari doesn't expose programmatic install).
 * - Dismissal persisted for 7 days so we don't nag.
 * - Auto-hides once the app is installed.
 */
export default function InstallPwaPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (wasRecentlyDismissed()) return;

    // Only show on mobile / tablet screens — desktops have their own install UI
    const isMobile = window.matchMedia?.("(max-width: 768px)").matches;
    if (!isMobile) return;

    function onBeforeInstall(e) {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    }
    function onInstalled() {
      setVisible(false);
      setDeferredPrompt(null);
    }

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // Safari (iOS) never fires beforeinstallprompt — show the manual hint
    if (isIos()) setVisible(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  function dismiss() {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* non-fatal */ }
    setVisible(false);
    setShowIosHelp(false);
  }

  async function handleInstall() {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice?.outcome === "accepted") {
        setVisible(false);
      }
      setDeferredPrompt(null);
      return;
    }
    if (isIos()) {
      setShowIosHelp(true);
    }
  }

  if (!visible) return null;

  return (
    <div
      data-testid="install-pwa-prompt"
      className="md:hidden rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50 to-emerald-50 shadow-sm p-4"
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-lg bg-blue-700 flex items-center justify-center shrink-0">
          <Smartphone className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-sm font-bold text-slate-900">
            Instale o BV no seu celular
          </h3>
          <p className="text-xs text-slate-600 mt-0.5 leading-snug">
            Acesso em tela cheia, sem barras de URL, ícone direto no menu — como um aplicativo nativo.
          </p>
          {!showIosHelp && (
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                onClick={handleInstall}
                className="bg-blue-700 hover:bg-blue-800 h-10 text-sm"
                data-testid="btn-install-pwa"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                {isIos() ? "Como instalar" : "Instalar agora"}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={dismiss}
                className="h-10 text-sm text-slate-600"
                data-testid="btn-dismiss-pwa"
              >
                Mais tarde
              </Button>
            </div>
          )}
          {showIosHelp && (
            <div className="mt-3 rounded-lg border border-blue-200 bg-white p-3 text-xs text-slate-700 space-y-1.5" data-testid="ios-install-help">
              <p className="font-semibold text-slate-900">Para instalar no iPhone:</p>
              <p className="flex items-center gap-1.5">
                <span className="font-bold text-blue-700">1.</span>
                Toque em <Share className="h-3.5 w-3.5 inline mx-0.5 text-blue-700" />
                <span className="font-medium">Compartilhar</span> (barra inferior do Safari)
              </p>
              <p className="flex items-center gap-1.5">
                <span className="font-bold text-blue-700">2.</span>
                Role e toque em
                <span className="font-medium">"Adicionar à Tela de Início"</span>
              </p>
              <p className="flex items-center gap-1.5">
                <span className="font-bold text-blue-700">3.</span>
                Confirme em <span className="font-medium">"Adicionar"</span>
              </p>
              <Button size="sm" variant="ghost" onClick={dismiss} className="h-9 mt-2 text-xs w-full">
                Entendi
              </Button>
            </div>
          )}
        </div>
        <button
          onClick={dismiss}
          className="text-slate-400 hover:text-slate-700 shrink-0"
          aria-label="Fechar"
          data-testid="btn-close-pwa-prompt"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
